import classNames from 'classnames';
import AllCountries from 'country-telephone-data';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React from 'react';

const propTypes = exact({
  blockName: PropTypes.string,
  fieldName: PropTypes.string,
  fieldValue: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isValid: PropTypes.bool,
  modifier: PropTypes.string,
  excludeDialCodes: PropTypes.bool,
  dialCodes: PropTypes.arrayOf(PropTypes.string),
});

const defaultProps = {
  blockName: null,
  fieldName: null,
  isDisabled: false,
  isInvalid: false,
  isValid: false,
  modifier: 'ghost',
  excludeDialCodes: true,
  dialCodes: [],
};

class Dropdown extends React.Component {
  constructor(props) {
    super(props);

    this.customScrolling = this.customScrolling.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleClickOutsideDropdown = this.handleClickOutsideDropdown.bind(this);
    this.handleKeyDownOnList = this.handleKeyDownOnList.bind(this);
    this.toggleDropdownOnBtn = this.toggleDropdownOnBtn.bind(this);

    this.dropdown = React.createRef();
    this.dropdownOptions = React.createRef();

    if (props.dialCodes.length > 0) {
      this.filteredCountries = props.excludeDialCodes
        ? AllCountries.allCountries.filter(
            (option) => !props.dialCodes.includes(`+${option.dialCode}`)
          )
        : AllCountries.allCountries.filter((option) =>
            props.dialCodes.includes(`+${option.dialCode}`)
          );
    } else {
      this.filteredCountries = AllCountries.allCountries;
    }

    document.body.addEventListener('mousedown', this.handleClickOutsideDropdown);

    let { fieldValue } = this.props;

    const foundCountry = this.filteredCountries.find((country) => country.dialCode === fieldValue);
    if (foundCountry) {
      fieldValue = foundCountry;
    } else if (AllCountries.iso2Lookup.gb in this.filteredCountries) {
      fieldValue = this.filteredCountries[AllCountries.iso2Lookup.gb];
    } else {
      fieldValue = this.filteredCountries[0];
    }

    this.state = {
      isActive: false,
      dropdownItemActiveIndex: 0,
      selectedItem: fieldValue,
      timeSeconds: new Date().getTime() / 1000,
      threshold: 1,
      search: '',
    };

    this.keyCode = {
      a: 65,
      z: 90,
      tab: 9,
      enter: 13,
      escape: 27,
      arrUp: 38,
      arrDown: 40,
    };
  }

  pickCountry(event, country) {
    if (event) {
      event.preventDefault();
      // eslint-disable-next-line
      event.currentTarget.name = event.currentTarget.name || this.props.fieldName;
      // eslint-disable-next-line
      event.currentTarget.value = country.dialCode;

      this.props.handleChange(event);
      this.setState({
        selectedItem: country,
        isActive: !this.state.isActive,
      });
    }
  }

  restartDropdownSettings(active) {
    this.dropdown.current.scrollTop = 0;
    this.setState(
      {
        isActive: active,
        dropdownItemActiveIndex: 0,
      },
      () => {
        if (this.dropdown.current) {
          this.dropdown.current.focus();
        }
      }
    );
  }

  handleClick(event) {
    event.preventDefault();
    this.restartDropdownSettings(!this.state.isActive);
  }

  // Set index to active, when user hovers over it.
  handleMouseEnterOnDropdownList(event, index) {
    this.setState({ dropdownItemActiveIndex: index });
  }

  customScrolling(height) {
    this.dropdown.current.scrollTop += height;
  }

  scrollToActiveListItem(activeIndex, countries) {
    const listContainer = this.dropdown.current;
    const listItemHeight = listContainer.scrollHeight / countries.length;
    const moveScrollbarTo = listItemHeight * activeIndex;

    listContainer.scrollTop = moveScrollbarTo;
  }

  filter(value) {
    const countries = this.filteredCountries;

    // filter the items, from unorderedList. This is filtering both whole and partial item names.
    const filterResults = countries.filter((country) => {
      const name = country.name.toLowerCase().replace(' ', '').substring(0, value.length);
      const newVal = value.toLowerCase().replace(' ', '');
      return name.includes(newVal);
    });

    const activeIndex = countries.indexOf(filterResults[0]);
    this.setState(
      { dropdownItemActiveIndex: activeIndex },
      this.scrollToActiveListItem(activeIndex, countries)
    );
  }

  handleSearch(value) {
    const { timeSeconds, threshold, search } = this.state;
    const timeNowSeconds = new Date().getTime() / 1000;
    const deltaTime = timeNowSeconds - timeSeconds;

    // If you typed new letter within time, concatenate search value and restart time.
    if (threshold > deltaTime) {
      const newSearchValue = search + value;

      this.setState(
        {
          search: newSearchValue,
          timeSeconds: new Date().getTime() / 1000,
        },
        this.filter(newSearchValue)
      );

      return;
    }

    // If time passed, replace the search with newly inputed value, and restart timer.
    this.setState(
      {
        search: value,
        timeSeconds: new Date().getTime() / 1000,
      },
      this.filter(value)
    );
  }

  handleKeyDownOnList(event) {
    const { keyCode } = this;
    const { dropdownItemActiveIndex: index, isActive } = this.state;
    if (isActive) {
      const countries = this.filteredCountries;

      // Arrow up and tab + shift
      if (event.keyCode === keyCode.arrUp || (event.shiftKey && event.keyCode === keyCode.tab)) {
        event.preventDefault();

        // Go up untill you hit first item, then turn off dropdown
        let newActiveIndex = 0;
        if (index > 0) {
          newActiveIndex = index - 1;
        } else if (event.shiftKey && event.keyCode === keyCode.tab && index === 0) {
          this.restartDropdownSettings(false);
          return;
        }

        this.setState({ dropdownItemActiveIndex: newActiveIndex });
        this.customScrolling(-this.dropdown.current.firstChild.offsetHeight);

        // Arrow down key and tab
      } else if (event.keyCode === keyCode.arrDown || event.keyCode === keyCode.tab) {
        event.preventDefault();

        // Go down untill you hit last item, then turn off dropdown
        let newActiveIndex = 0;
        if (index < countries.length - 1) {
          newActiveIndex = index + 1;
        } else {
          this.restartDropdownSettings(false);
          return;
        }

        this.setState({ dropdownItemActiveIndex: newActiveIndex });
        this.customScrolling(this.dropdown.current.firstChild.offsetHeight);

        // Allowing only letters
      } else if (event.keyCode >= keyCode.a && event.keyCode <= keyCode.z) {
        this.handleSearch(event.key);
      } else if (event.keyCode === keyCode.escape) {
        this.restartDropdownSettings(false);
      } else if (event.keyCode === keyCode.enter) {
        this.pickCountry(event, countries[index]);
      }
    }
  }

  handleClickOutsideDropdown(event) {
    const { isActive } = this.state;

    // Check if click is soutside of dropdownOptions(ul) and button.
    if (this.dropdownOptions.current && this.dropdown.current) {
      const insideUl = this.dropdownOptions.current.contains(event.target);
      const insideBtn = this.dropdown.current.contains(event.target);
      if (this.dropdownOptions && !insideUl && !insideBtn && isActive) {
        this.restartDropdownSettings(false);
      }
    }
  }

  toggleDropdownOnBtn(event) {
    const { isActive } = this.state;
    const { keyCode } = this;

    // whithout this line when you navigate whit tab/shift+ tab, list opens when it should not.
    if (!(event.shiftKey && event.keyCode === keyCode.tab)) {
      this.restartDropdownSettings(!isActive);
    }
  }

  render() {
    const { isActive, selectedItem, dropdownItemActiveIndex } = this.state;
    const { isDisabled, isInvalid, isValid, modifier, fieldName } = this.props;

    const dropdownBtnClasses = (index) =>
      classNames('InternationalPhoneCodeDropdown_optionButton', {
        active: index === dropdownItemActiveIndex,
      });

    const dropdownClasses = classNames(
      this.props.blockName,
      'InternationalPhoneCodeDropdown',
      `InternationalPhoneCodeDropdown-${modifier}`,
      {
        active: isActive,
        disabled: isDisabled,
        invalid: isInvalid,
        valid: isValid,
      }
    );

    return (
      <div className={dropdownClasses}>
        <div
          name={fieldName}
          disabled={isDisabled}
          className="InternationalPhoneCodeDropdown_select"
          onClick={this.handleClick}
          ref={this.dropdownOptions}
          role="button"
          onKeyDown={this.toggleDropdownOnBtn}
          tabIndex="0"
        >
          <div
            className="InternationalPhoneCodeDropdown_selectedFlagIcon"
            style={{ backgroundImage: `url(../dist/images/flags/${selectedItem.iso2}.png)` }}
          />
          <div className="InternationalPhoneCodeDropdown_selectedText">
            +{selectedItem.dialCode}
          </div>
        </div>
        <ul
          className="InternationalPhoneCodeDropdown_options"
          ref={this.dropdown}
          tabIndex="0"
          onKeyDown={this.handleKeyDownOnList}
        >
          {this.filteredCountries.map((option, index) => (
            // eslint-disable-next-line
            <li className="InternationalPhoneCodeDropdown_option" key={option.iso2} tabIndex="0">
              <button
                tabIndex="-1"
                className={dropdownBtnClasses(index)}
                name={fieldName}
                onClick={(event) => this.pickCountry(event, option)}
                onMouseEnter={(event) => this.handleMouseEnterOnDropdownList(event, index)}
              >
                <div
                  className="InternationalPhoneCodeDropdown_flagIcon"
                  // eslint-disable-next-line
                  style={{ backgroundImage: `url(../dist/images/flags/${option.iso2}.png)` }}
                />
                <span className="InternationalPhoneCodeDropdown_countryName">{` ${option.name}`}</span>
                <span className="InternationalPhoneCodeDropdown_dialCode">{` +${option.dialCode}`}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

Dropdown.propTypes = propTypes;
Dropdown.defaultProps = defaultProps;

export default Dropdown;
